import CryptoJS from "crypto-js";
import { jwtDecode } from "jwt-decode";
import { decryptData as decryptAuthData } from "./encryptionUtils";

export const getTokens = () => {
    const storedAuth = localStorage.getItem("auth");

    if (storedAuth) {
        const decryptedTokens = decryptAuthData(storedAuth);
        return decryptedTokens;
    }

    return null;
};

export const decodeJWT = (token) => {
    try {
        // Decode the token to extract payload
        const decoded = jwtDecode(token);
        return decoded;
    } catch (error) {
        console.error("Invalid token:", error.message);
        return null;
    }
};

export const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
        .then(() => {
            // console.log("Text copied to clipboard successfully!");
        })
        .catch((err) => {
            console.error("Failed to copy text: ", err);
        });
};

export const generateUniqueId = (length = 8) => {
    const randomNumber = Math.floor(Math.random() * Math.pow(10, length));
    return String(randomNumber).padStart(length, '0'); // Ensures the string has leading zeros if needed
};

export const encryptData = ({ secretKey, token }) => {
    const encryptedToken = CryptoJS.AES.encrypt(JSON.stringify(token), secretKey).toString();
    return encryptedToken;
};

export const decryptData = ({ secretKey, token }) => {
    try {
        const bytes = CryptoJS.AES.decrypt(token, secretKey);
        const decryptedToken = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        return decryptedToken;
    } catch (error) {
        console.error("Decryption error:", error);
        return null; // Return null or handle the error as needed
    }
};

export const printFormData = (formData) => {
    const data = {};
    formData.forEach((value, key) => {
        data[key] = value;
    });
    console.log(data);
};

export const cleanMarkdown = (text) => {

    // Trim trailing whitespace or newline characters before processing
    const trimmedText = text.trim();

    // Check if the response starts with ```Markdown and ends with ```
    if ((trimmedText.startsWith('```Markdown') || trimmedText.startsWith('```markdown')) && trimmedText.endsWith('```')) {
        // Extract the content between the starting and ending ```
        const content = trimmedText.slice(11, -3); // Remove ```Markdown (11 chars) from start and ``` (3 chars) from end

        // Check if there are no additional ``` in the content
        if (!content.includes('```')) {
            return content.trim(); // Remove the start and end markers and return the cleaned content
        }
    } else if ((trimmedText.startsWith('```Model') || trimmedText.startsWith('```model')) && trimmedText.endsWith('```')) {
        // Extract the content between the starting and ending ```
        const content = trimmedText.slice(8, -3); // Remove ```Model (8 chars) from start and ``` (3 chars) from end

        // Check if there are no additional ``` in the content
        if (!content.includes('```')) {
            return content.trim(); // Remove the start and end markers and return the cleaned content
        }
    }

    // Regular expression to match code blocks with language identifiers (e.g., ```python, ```json)
    const codeBlockPattern = /```(\w+)\n([\s\S]*?)```/g;

    // Preserve code blocks by matching and replacing them with placeholders
    let codeBlockMatches = [];
    let cleanedText = text.replace(codeBlockPattern, (match, language, code) => {
        // Save the matched code block and replace it with a placeholder
        const placeholder = `{{CODE_BLOCK_${codeBlockMatches.length}}}`;
        codeBlockMatches.push({ language, code });
        return placeholder;
    });

    // Now clean the rest of the markdown content (outside code blocks)
    // Remove extra backticks from text that isn't a code block
    cleanedText = cleanedText.replace(/`{3,}/g, ''); // Remove any occurrence of triple backticks or more

    // Replace placeholders back with the actual code blocks
    cleanedText = cleanedText.replace(/{{CODE_BLOCK_(\d+)}}/g, (match, index) => {
        const codeBlock = codeBlockMatches[index];
        return `\`\`\`${codeBlock.language}\n${codeBlock.code}\n\`\`\``;
    });

    return cleanedText;
};


export const handleMarkdownConversion = (conversionFn, fileExtension, dataItem) => {
    if (!dataItem?.response_data) return;

    const id = `glassyfied-${generateUniqueId(8)}`; // Generate a unique ID

    conversionFn(
        { id, markdown: dataItem.response_data },
        {
            onSuccess: (blob) => {
                // Convert the blob to an object URL and initiate the download
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `${id}.${fileExtension}`; // Use the appropriate file extension
                link.click();
                URL.revokeObjectURL(link.href); // Clean up URL
            },
            onError: (error) => {
                console.error(`Failed to convert Markdown to ${fileExtension.toUpperCase()}:`, error);
            },
        }
    );
};

export const handleHtmlConversion = (conversionFn, fileExtension, content) => {
    if (!content) return;

    const id = `glassyfied-${generateUniqueId(8)}`; // Generate a unique ID

    conversionFn(
        { id, html: content }, // Pass content directly as HTML
        {
            onSuccess: (blob) => {
                // Create a download link and initiate download
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `${id}.${fileExtension}`; // Use appropriate file extension
                link.click();
                URL.revokeObjectURL(link.href); // Clean up URL
            },
            onError: (error) => {
                console.error(`Failed to convert HTML to ${fileExtension.toUpperCase()}:`, error);
            },
        }
    );
};


// Utility function to extract a specific claim
export const extractClaim = (token, key) => {
    if (!token) {
        // console.error("Token is undefined or null");
        return null;
    }
    try {
        const decodedToken = jwtDecode(token);

        // Check if the key exists in the token
        if (decodedToken[key] !== undefined) {
            return decodedToken[key];
        } else {
            throw new Error(`Key "${key}" not found in token`);
        }
    } catch (error) {
        throw new Error(`Error decoding token: ${error.message}`);
    }
};